import { useState, useEffect, useCallback, useRef } from 'react'
import {
  Box,
  Typography,
  Button,
  Paper,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  Tooltip,
  Skeleton,
} from '@mui/material'
import {
  CloudUpload as UploadIcon,
  PictureAsPdf as PdfIcon,
  Article as TextIcon,
  Download as DownloadIcon,
  OpenInNew as OpenIcon,
  Delete as DeleteIcon,
  InsertDriveFile as FileIcon,
} from '@mui/icons-material'
import { attachmentsApi, Attachment, Client } from '../../services/api'
import { useNotification } from '../../hooks/useNotification'

interface ClientResumeUploadTabProps {
  client: Client
  onUpdate: (client: Partial<Client>) => void
  isPortal?: boolean
}

function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes'
  const k = 1024
  const sizes = ['Bytes', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export default function ClientResumeUploadTab({ client, isPortal }: ClientResumeUploadTabProps) {
  const [resume, setResume] = useState<Attachment | null>(null)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [dragOver, setDragOver] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const { showSuccess, showError } = useNotification()

  const loadResume = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const attachments = await attachmentsApi.list(client.id, 'resume')
      const resumeFiles = attachments.filter(
        (a) => a.contentType === 'application/pdf' || a.contentType.startsWith('text/')
      )
      if (resumeFiles.length > 0) {
        const latest = resumeFiles[0]
        setResume(latest)
        const { downloadUrl } = await attachmentsApi.generateDownloadUrl(latest.id, 'preview')
        setPreviewUrl(downloadUrl)
      } else {
        setResume(null)
        setPreviewUrl(null)
      }
    } catch (err) {
      console.error('Failed to load resume:', err)
      setError("Couldn't load the resume. Try refreshing the page.")
    } finally {
      setLoading(false)
    }
  }, [client.id])

  useEffect(() => {
    loadResume()
  }, [loadResume])

  const validateFile = (file: File): string | null => {
    if (file.size > 10 * 1024 * 1024) {
      return 'The file did not upload. Check the file size is under 10MB and try again.'
    }
    if (file.type !== 'application/pdf' && !file.type.startsWith('text/')) {
      return 'Please upload a PDF or text file'
    }
    return null
  }

  const uploadFile = async (file: File) => {
    const validationError = validateFile(file)
    if (validationError) {
      showError(validationError)
      return
    }

    try {
      setUploading(true)
      setError(null)
      const attachment = await attachmentsApi.upload(file, 'resume', client.id)
      setResume(attachment)
      const { downloadUrl } = await attachmentsApi.generateDownloadUrl(attachment.id, 'preview')
      setPreviewUrl(downloadUrl)
      showSuccess('Resume uploaded successfully')
    } catch (err) {
      console.error('Failed to upload resume:', err)
      setError('Failed to upload resume. Please try again.')
    } finally {
      setUploading(false)
    }
  }

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) uploadFile(file)
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    setDragOver(true)
  }

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    setDragOver(false)
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setDragOver(false)
    const file = e.dataTransfer.files?.[0]
    if (file) uploadFile(file)
  }

  const handleDownload = async () => {
    if (!resume) return
    try {
      await attachmentsApi.download(resume.id)
    } catch (err) {
      console.error('Failed to download resume:', err)
      showError('Failed to download resume')
    }
  }

  const handleOpenNewTab = async () => {
    if (!resume) return
    try {
      await attachmentsApi.preview(resume.id)
    } catch (err) {
      console.error('Failed to open resume:', err)
      showError('Failed to open resume')
    }
  }

  const handleDelete = async () => {
    if (!resume) return
    try {
      await attachmentsApi.delete(resume.id)
      setResume(null)
      setPreviewUrl(null)
      showSuccess('Resume deleted')
    } catch (err) {
      console.error('Failed to delete resume:', err)
      showError('Failed to delete resume')
    }
  }

  if (loading) {
    return (
      <Box sx={{ py: 2 }}>
        <Skeleton variant="rounded" height={120} sx={{ mb: 2 }} />
        <Skeleton variant="text" width="40%" height={24} />
      </Box>
    )
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {uploading && (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 8, gap: 2 }}>
          <CircularProgress sx={{ color: '#C41E3D' }} />
          <Typography color="text.secondary">Uploading resume...</Typography>
        </Box>
      )}

      {!uploading && !resume && (
        <Paper
          variant="outlined"
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          sx={{
            p: 6,
            textAlign: 'center',
            cursor: 'pointer',
            borderStyle: 'dashed',
            borderWidth: 2,
            borderColor: dragOver ? 'primary.main' : 'divider',
            backgroundColor: dragOver ? 'action.hover' : 'transparent',
            transition: 'all 0.2s',
            '&:hover': {
              borderColor: 'primary.main',
              backgroundColor: 'action.hover',
            },
          }}
          onClick={() => fileInputRef.current?.click()}
        >
          <UploadIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            {isPortal ? 'Upload Your Resume' : 'Upload Client Resume'}
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 2 }}>
            Drag and drop a PDF or text file here, or click to browse
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Maximum file size: 10MB
          </Typography>
          <input
            ref={fileInputRef}
            type="file"
            hidden
            onChange={handleFileSelect}
            accept=".pdf,.txt"
          />
        </Paper>
      )}

      {!uploading && resume && (
        <Box>
          {/* File identity and its actions are both content-width, so the row
              wraps rather than pushing the actions past the column edge. */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 2,
              mb: 2,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0, gap: 1.5 }}>
              {resume.contentType === 'application/pdf' ? (
                <PdfIcon color="primary" />
              ) : (
                <TextIcon color="primary" />
              )}
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {resume.fileName}
                  </Typography>
                  <Chip
                    label={formatFileSize(resume.fileSize)}
                    size="small"
                    variant="outlined"
                    sx={{ height: 20, fontSize: '0.7rem' }}
                  />
                </Box>
                <Typography variant="caption" color="text.secondary">
                  Uploaded by {resume.uploadedByName || resume.uploadedBy} on {formatDate(resume.createdAt)}
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<UploadIcon />}
                component="label"
              >
                Replace
                <input
                  type="file"
                  hidden
                  onChange={handleFileSelect}
                  accept=".pdf,.txt"
                />
              </Button>
              <Tooltip title="Open in new tab">
                <IconButton size="small" onClick={handleOpenNewTab} color="primary">
                  <OpenIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Download">
                <IconButton size="small" onClick={handleDownload}>
                  <DownloadIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              {!isPortal && (
                <Tooltip title="Delete">
                  <IconButton size="small" onClick={handleDelete} color="error">
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          </Box>

          {previewUrl && resume.contentType === 'application/pdf' ? (
            <Paper variant="outlined" sx={{ overflow: 'hidden', borderRadius: 1 }}>
              <iframe
                src={previewUrl}
                title="Resume Preview"
                // A native PDF viewer can't report its own content height, so
                // this can't size exactly to the document. Instead of nearly
                // filling the viewport (calc(100vh - 300px)) regardless of
                // page count — which left a large dead area below a short
                // resume — this caps at a sensible height and lets the
                // viewer's own scrollbar handle anything longer.
                style={{ width: '100%', height: 'clamp(480px, 65vh, 820px)', border: 'none' }}
              />
            </Paper>
          ) : previewUrl && resume.contentType.startsWith('text/') ? (
            <Paper variant="outlined" sx={{ p: 3 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Text file preview not available inline.
              </Typography>
              <Button variant="outlined" size="small" startIcon={<OpenIcon />} onClick={handleOpenNewTab}>
                Open in new tab
              </Button>
            </Paper>
          ) : (
            <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
              <FileIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
              <Typography color="text.secondary">Preview not available</Typography>
            </Paper>
          )}
        </Box>
      )}
    </Box>
  )
}
